export type ImportDocumentStatus = 'pending' | 'processing' | 'ready' | 'failed' | 'discarded' | 'confirmed';

export type ImportFieldOption =
    | string
    | number
    | { option?: string; price?: number | string; label?: string; value?: string };

/** A form field the AI can fill, as the backend hands it out with the document list. */
export interface ImportField {
    name: string;
    label?: string;
    type: string;
    options?: ImportFieldOption[];
    selectionMode?: 'single' | 'multiple';
    rules?: unknown[];
    /** Table fields: row and column labels, and which cells cannot be ticked. */
    rows?: string[];
    columns?: string[];
    disabledTimes?: boolean[][];
}

export interface ImportValues {
    name: string | null;
    email: string | null;
    form_data: Record<string, any>;
}

export interface ImportDocument {
    id: number;
    status: ImportDocumentStatus;
    original_name: string | null;
    mime_type: string | null;
    sort_order: number;
    error_code: string | null;
    error_message: string | null;
    /** The AI's untouched answer, only once it is there. */
    extraction?: ImportValues | null;
    values: ImportValues | null;
    /** Validation messages keyed like `email` or `form_data.<field>`. Empty means bookable. */
    errors: Record<string, string[]>;
    /** Required by the online form but left empty. Shown, never blocking: admins may book anyway. */
    warnings: Record<string, string[]>;
    booking_id: number | null;
    created_at: string;
    processed_at: string | null;
}

/** A value patch as the update endpoints take it. */
export interface ImportValuesPatch {
    name?: string | null;
    email?: string | null;
    form_data?: Record<string, any>;
}

export interface CommitResult {
    created: { document_id: number; booking_id: number }[];
    failed: Record<number, Record<string, string[]>>;
}

/** Column key: `name`, `email` or `form_data.<field>`. */
export type ColumnKey = string;

export const isBookable = (document: ImportDocument) =>
    ('ready' === document.status || 'failed' === document.status) && Object.keys(document.errors ?? {}).length === 0;

export const readValue = (values: ImportValues | null | undefined, key: ColumnKey): any => {
    if (!values) return null;
    if ('name' === key || 'email' === key) return values[key];

    return values.form_data?.[key.replace(/^form_data\./, '')] ?? null;
};

export const patchFor = (key: ColumnKey, value: any): ImportValuesPatch =>
    'name' === key || 'email' === key ? { [key]: value } : { form_data: { [key.replace(/^form_data\./, '')]: value } };

// Forms store options as strings, as numbers (e.g. [1, 2, 3]) or as objects for payment options.
export const optionLabel = (option: ImportFieldOption): string =>
    typeof option === 'object' && null !== option
        ? String(option.option ?? option.label ?? option.value ?? '')
        : String(option ?? '');
